<template>
  <div class="ask-ai-modal-wrapper">
    <div class="modal fancy-scroll">
      <div class="modal-close" @click="close"></div>

      <div class="content">
        <div class="tab">
          <div v-if="!getLogic && !getStyles" class="input-wrapper">
            Bitte auf ein Element klicken, um Änderungen mit KI durchzuführen
          </div>
          <div v-else class="ask-ai-accordion-stack">
            <FormAICodeAccordion
              v-if="getAIStyleList?.length"
              title="AI Style"
              codeType="style"
              :aiItemList="getAIStyleList"
              :elements="elements"
              :isActive="isAccordionActive('style')"
              :shouldFillHeight="shouldStretchAccordion('style')"
              @toggle="onAccordionToggle('style', $event)"
              @onRemove="onRemoveAICode"
              @onEdit="onEditAICode"
            />
            <FormAICodeAccordion
              v-if="getAICodeList?.length"
              title="AI Logik"
              codeType="logic"
              :aiItemList="getAICodeList"
              :elements="elements"
              :isActive="isAccordionActive('logic')"
              :shouldFillHeight="shouldStretchAccordion('logic')"
              @toggle="onAccordionToggle('logic', $event)"
              @onRemove="onRemoveAICode"
              @onEdit="onEditAICode"
            />
          </div>
        </div>
      </div>
      <FloatingAskAiDropdown
        v-if="selectedElId && editAITaskIndex !== null"
        :processId="processId"
        :isEditMode="true"
        :editTaskIndex="editAITaskIndex"
        :task="task"
        :elements="elements"
        :selectedId="selectedElId"
        :flowRunnerEndpoint="flowRunnerEndpoint"
        :isLoading="false"
        @onClose="onCloseFloatingDropdown"
      />
    </div>
  </div>
</template>

<script>
// components
import FormAICodeAccordion from '@/components/FormBuilder/FormAIcodeAccordion.vue';
import {
  executeAIGeneratedStyle,
  handleAskAI,
  getParsedAIResponse,
  createAIGeneratedEntry,
  resolveAIEntryIndex,
} from '@/utils/helpers/FormBuilderHelper.js';

export default {
  name: 'AskAImodal',

  data() {
    return {
      prompt: '',
      promptSuggetions: [],
      editAITaskIndex: null,
      selectedElId: null,
      activeAIAccordion: null,
      prevHasAIStyles: null,
      prevHasAILogic: null,
    };
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    processId: {
      type: String,
      required: true,
    },
    elements: {
      type: Array,
      required: true,
    },
    task: {
      type: Object,
      required: true,
    },
    flowRunnerEndpoint: {
      type: String,
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    async onAskAiClick(prompt) {
      if (this.isLoading || !prompt) return;
      this.$emit('toggleloading', true);
      try {
        const response = await handleAskAI(
          this.task,
          prompt,
          this.flowRunnerEndpoint
        );

        const parseRes = getParsedAIResponse(response, this.task);

        if (
          !('aiGenerated' in this.task.props.settings) ||
          !Array.isArray(this.task.props.settings.aiGenerated)
        ) {
          this.$set(this.task.props.settings, 'aiGenerated', [
            createAIGeneratedEntry(parseRes, prompt, this.selectedElId),
          ]);
        } else {
          this.task.props.settings.aiGenerated.push(
            createAIGeneratedEntry(parseRes, prompt, this.selectedElId)
          );
        }
        executeAIGeneratedStyle(this.getStyles);
      } catch (error) {
        this.$emit('showToaster', error.message, 'Fehler', 'danger');
      } finally {
        this.$emit('toggleloading', false);
      }
    },
    onRemoveAICode(codeType, entryId) {
      const entryIndex = resolveAIEntryIndex(
        this.task.props.settings.aiGenerated,
        entryId
      );
      if (entryIndex < 0) return;
      const removeItem = this.task.props.settings.aiGenerated[entryIndex];
      if (removeItem && removeItem.styles && removeItem.code) {
        if (codeType === 'logic') {
          this.$set(removeItem, 'code', '');
        } else if (codeType === 'style') {
          this.$set(removeItem, 'styles', '');
        }
      } else {
        this.task.props.settings.aiGenerated.splice(entryIndex, 1);
      }
      executeAIGeneratedStyle(this.getStyles);
    },
    onEditAICode(entryId, elementId) {
      this.editAITaskIndex = resolveAIEntryIndex(
        this.task.props.settings.aiGenerated,
        entryId
      );
      this.selectedElId = elementId;
    },
    onCloseFloatingDropdown() {
      this.editAITaskIndex = null;
      this.selectedElId = null;
    },
    onAccordionToggle(section, shouldOpen) {
      if (shouldOpen) {
        this.activeAIAccordion = section;
        return;
      }

      if (this.activeAIAccordion === section) {
        this.activeAIAccordion = null;
      }
    },
    shouldStretchAccordion(section) {
      return this.activeAIAccordion === section;
    },
    isAccordionActive(section) {
      return this.activeAIAccordion === section;
    },
    sectionExists(section) {
      if (section === 'style') return this.hasAIStyles;
      if (section === 'logic') return this.hasAILogic;
      return false;
    },
    ensureActiveAccordion() {
      if (
        this.activeAIAccordion &&
        this.sectionExists(this.activeAIAccordion)
      ) {
        return;
      }
      this.activeAIAccordion = null;
    },
  },

  computed: {
    getLogic() {
      return this.task.props?.settings?.aiGenerated
        ?.map((aiItem) => aiItem?.code)
        .join('\n\n');
    },
    getAIGenerateList() {
      return this.task.props?.settings?.aiGenerated || [];
    },
    getAIStyleList() {
      return this.getAIGenerateList
        ?.map((item, index) => ({
          ...item,
          entryId: item?.id || index,
          mainIndex: index,
        }))
        .filter((item) => item.styles);
    },
    getAICodeList() {
      return this.getAIGenerateList
        ?.map((item, index) => ({
          ...item,
          entryId: item?.id || index,
          mainIndex: index,
        }))
        .filter((item) => item.code);
    },
    getLogicExplanation() {
      return this.task.props?.settings?.aiGenerated?.explanation;
    },
    getStyles() {
      return this.task.props?.settings?.aiGenerated
        ?.map((aiItem) => aiItem?.styles)
        .join('\n\n');
    },
    getStylesExplanation() {
      return this.task.props?.settings?.aiGenerated?.styleExplanation;
    },
    hasAIStyles() {
      return Boolean(this.getAIStyleList?.length);
    },
    hasAILogic() {
      return Boolean(this.getAICodeList?.length);
    },
  },
  mounted() {
    this.prevHasAIStyles = this.hasAIStyles;
    this.prevHasAILogic = this.hasAILogic;
    this.ensureActiveAccordion();
  },
  updated() {
    if (
      this.prevHasAIStyles !== this.hasAIStyles ||
      this.prevHasAILogic !== this.hasAILogic
    ) {
      this.ensureActiveAccordion();
      this.prevHasAIStyles = this.hasAIStyles;
      this.prevHasAILogic = this.hasAILogic;
    }
  },
  components: {
    FormAICodeAccordion,
    FloatingAskAiDropdown: () =>
      import('@/components/FormBuilder/AskAI/FloatingAskAiDropdown.vue'),
  },
};
</script>

<style scoped>
.ask-ai-modal-wrapper {
  width: fit-content;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.ask-ai-modal-wrapper .loading-spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding-right: 61px;
}

.ask-ai-modal-wrapper .modal {
  text-align: left;
  border-left: 1px solid #dee3e9;
  border-left-width: 1px;
  border-left-style: solid;
  border-left-color: rgb(222, 227, 233);
  border-radius: 0px 19px 19px 0px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  text-align: left;
  font-size: 12px;
  line-height: 17px;
  color: #5f697a;
  height: 100%;
  position: relative;
  width: 425px;
  overflow: hidden;
}

.ask-ai-modal-wrapper .content {
  background-color: #fff;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.ask-ai-modal-wrapper .tab {
  text-align: left;
  border-top: 1px solid #eaecf1;
  background: #fff;
  padding: 70px 40px 2px 40px;
  height: 100%;
  font-size: 13px;
  margin: 0;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}

.ask-ai-modal-wrapper label {
  display: block;
  padding-top: 0;
  padding-bottom: 9px;
  margin-right: 10px;
  margin-bottom: 0px;
  font-size: 18px;
  -webkit-font-smoothing: antialiased;
  box-shadow: none;
  box-sizing: border-box;
  width: 100%;
}
.ask-ai-modal-wrapper textarea {
  resize: none;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  padding-bottom: 15px;
  margin-bottom: 0;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 20px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  height: 200px;
  width: 100%;
  overflow: scroll;
}

.ask-ai-modal-wrapper .btn {
  text-decoration: none;
  color: #fff;
  border: 3px solid #000;
  border-radius: 0.375em;
  cursor: unset;
  display: inline-block;
  font-family: proxima-nova, Helvetica Neue, Arial, sans-serif;
  font-weight: 700;
  line-height: 23px;
  padding: 0.5rem 1.5rem;
  white-space: pre;
  font-size: 0.9375rem;
  opacity: 1;
  transition: transform 0.2s linear 0s, background-color 0.2s linear 0s;
  transform: translateY(0) scale(1.001);
  background-color: #000;
  margin-bottom: 25px;
  margin-right: 15px;
  cursor: pointer;
}

.ask-ai-modal-wrapper .value-preview {
  position: relative;
}
.ask-ai-modal-wrapper .code-toggle {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  width: 40px;
  height: 40px;
  background: white;
  border-radius: 50%;
  padding: 8px;
  box-shadow: 0px 0px 4px -2px #000;
  cursor: pointer;
}
.ask-ai-modal-wrapper .code-toggle img {
  width: 100%;
  height: 100%;
}
.ask-ai-modal-wrapper .input-wrapper {
  color: #9b9b9b;
  font-size: 14px;
  font-weight: 500;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  line-height: 17px;
}
.ask-ai-accordion-stack {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 2px;
  padding-right: 2px;
  padding-left: 2px;
}
</style>
